import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  ListItem,
  Tooltip,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import DataTypeConversionDropZone, {
  sourceTypeField,
} from '../DropZones/DataTypeConversionDropZone';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../redux';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  addResultantDatatypeInCalculationFlow,
  addSource,
  deleteField,
  deleteSourceV3,
  fieldDataType,
  resetSource,
  setToggleButton,
  setToggleVar,
  setTypeSelectionDropdown,
} from '../../../redux/Calculations/CalculationsActions';
import { fontSize, palette } from '../../..';
import { setSource } from '../../../redux/Calculations/CalculationsActions';
import { formatDateToYYYYMMDD, isFlowUsed } from '../utils';
import { Divider } from '@mui/material';
import { checkIfMissingColumn, handleKeyDown } from '../../CommonFunctions/CommonFunctions';
import { display } from 'html2canvas/dist/types/css/property-descriptors/display';
import FormatBuilder from '../DateControls/FormatBuilder';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useViewport } from '@xyflow/react';
interface TypeConversionProps {
  propKey: string;
}

export function TypeConversion({ propKey }: TypeConversionProps) {
  const dispatch = useDispatch();
  const { zoom } = useViewport();
  const { calculationInfo, activeFlow, flowNames, tablesForSelectedDataSets, chartProperties } =
    useSelector((state: RootState) => {
      const currCalc = state.calculations.properties[propKey]?.currentCalculationSession;
      return {
        calculationInfo: currCalc?.calculationInfo || null,
        activeFlow: currCalc?.activeFlow || null,
        flowNames: currCalc?.flowDisplayNames || {},
        tablesForSelectedDataSets: state.tabTileProps.tablesForSelectedDataSets,
        chartProperties: state.chartProperties,
      };
    });

  const [isShowWarning, setIsShowWarning] = useState<boolean>(false);
  const selectedDs = chartProperties.properties[propKey].selectedDs;

  const disableUpdate = useMemo(() => {
    if (!calculationInfo || !activeFlow) return true;
    return isFlowUsed(
      Object.keys(calculationInfo.flows),
      calculationInfo.flows,
      activeFlow,
      calculationInfo,
    );
  }, [calculationInfo, activeFlow]);

  const conversionOptions = ['text', 'Integer', 'Decimal', 'Date', 'Boolean'];
  const [toggleInput, setToggleInput] = useState('');
  const [_value, setValue] = useState(new Date().toISOString().split('T')[0]);

  const dataType = useMemo(() => {
    if (!calculationInfo || !activeFlow) return null;
    const currentFlow = calculationInfo.flows[activeFlow];
    const source = currentFlow[0]?.source;
    const sourceType = currentFlow[0]?.sourceType;

    if (!source[0]) return null;

    if (sourceType[0] === 'field') {
      return calculationInfo?.fields[source[0]!].dataType ?? null;
    } else {
      const flowName = calculationInfo?.flows?.[source[0]]?.[0]?.flow;
      const dataType =
        flowName === 'IfElse'
          ? calculationInfo?.selectedResultType?.[source?.[0] as string]
          : (calculationInfo?.flows?.[source?.[0] as string]?.[0]?.resultantDatatype ?? null);

      return dataType ?? null;
    }
  }, [calculationInfo]);

  const currentFlow = calculationInfo?.flows[activeFlow ?? ''];
  const source = currentFlow?.[0]?.source;
  const sourceType = currentFlow?.[0]?.sourceType;

  const changeHandler = (event: SelectChangeEvent) => {
    const newType = event.target.value;

    if (!activeFlow || !source) return;

    if (!disableUpdate) {
      dispatch(
        addResultantDatatypeInCalculationFlow(
          { uid: activeFlow, flow: activeFlow },
          propKey,
          newType.toLowerCase(),
          0,
        ),
      );
      dispatch(setSource(propKey, activeFlow, 0, newType.toLowerCase(), 'text', 1));
      dispatch(setSource(propKey, activeFlow, 0, source[2] ?? false, 'boolean', 2));

      dispatch(
        setSource(
          propKey,
          activeFlow,
          0,
          newType.toLowerCase() === 'boolean' && source[2] === true
            ? false
            : newType.toLowerCase() === 'date' && source[2] === true
              ? new Date().toISOString().split('T')[0]
              : '',
          newType.toLowerCase(),
          3,
        ),
      );

      if (newType !== 'date') {
        for (let i = source.length - 1; i >= 4; i--) {
          dispatch(deleteSourceV3(propKey, activeFlow, 0, i));
        }
      }
    }
    // console.log('source[1]', source[1], 'source[2]', source[2]);
  };
  const getInfo = (id: string, type: string) => {
    if (!id || !type || !calculationInfo) return null;
    if (type === 'field') {
      return {
        name:
          calculationInfo.fields[id]?.displayName || calculationInfo.fields[id]?.fieldName || id,
      };
    } else if (type === 'flow')
      return {
        name: flowNames[id] ?? id,
        dataType: calculationInfo.flows[id]?.[0]?.resultantDatatype || 'unknown',
      };
    return null;
  };

  const handleToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    if (!activeFlow || !source) return;

    if (!disableUpdate) {
      dispatch(setSource(propKey, activeFlow, 0, checked, 'boolean', 2));
      dispatch(setSource(propKey, activeFlow, 0, null, source[1] ?? 'text', 3));
      if (checked && source[1] === 'boolean') {
        dispatch(setSource(propKey, activeFlow, 0, false, source[1] ?? 'text', 3));
      }
      if (checked && source[1] === 'date') {
        dispatch(setSource(propKey, activeFlow, 0, _value, source[3] ?? 'text', 3));
      }
    }
  };

  const toggleInputHandler = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const inputValue = event.target.value;
    setToggleInput(inputValue);
    dispatch(setToggleVar(propKey, inputValue));
    if (!activeFlow) return;

    var inputValueType;
    if (/^[0-9]+$/.test(inputValue)) {
      inputValueType = 'integer';
    } else if (/^-?\d+?\.\d+$/.test(inputValue)) {
      inputValueType = 'decimal';
    } else if (/^[a-z ]+$/.test(inputValue)) {
      inputValueType = 'text';
    } else {
      inputValueType = '';
    }

    if (!disableUpdate) {
      dispatch(setSource(propKey, activeFlow, 0, inputValue, inputValueType, 3));
    }
  };

  const handleBoolean = (event: React.ChangeEvent<HTMLInputElement>) => {
    const boolCheck = event.target.checked;

    if (!activeFlow || !source) return;
    dispatch(setSource(propKey, activeFlow, 0, boolCheck, source[1], 3));
  };

  const updateFormatValue = (idx: number | undefined, value: any, _delete: boolean = false) => {
    const index = idx !== undefined ? idx + 4 : idx;

    if (!activeFlow) return;

    if (_delete && index) dispatch(deleteSourceV3(propKey, activeFlow, 0, index));
    else dispatch(addSource(propKey, activeFlow, 0, value, 'text', index));
  };
  const getSeperator = (source: any): string[] => {
    const exclude = ['%Y', '%y', '%M', '%b', '%c', '%m', '%e', '%d', '%D'];
    return source.slice(1).filter((value: string) => !exclude.includes(value));
  };
  function isOptionDisabled(dataType: string | null, option: string) {
    const dt = dataType?.toLowerCase();
    const opt = option.toLowerCase();

    if (!dt) return false;
    if (dt === opt) return true;
    return (
      (dt === 'boolean' && (opt === 'date' || opt === 'decimal')) ||
      (dt === 'decimal' && (opt === 'boolean' || opt === 'date')) ||
      (dt === 'date' && (opt === 'decimal' || opt === 'boolean' || opt === 'integer')) ||
      (dt === 'timestamp' && (opt === 'decimal' || opt === 'boolean' || opt === 'integer'))
    );
  }

  useEffect(() => {
    if (!sourceType || sourceType[0] !== 'field' || !source || !source[0]) return;

    (async () => {
      try {
        const fields = calculationInfo?.fields;
        if (!fields) return;
        const res = await checkIfMissingColumn(
          fields[source[0]],
          selectedDs,
          tablesForSelectedDataSets[selectedDs.id],
        );
        setIsShowWarning(res.isMissing);
      } catch (error) {
        console.error('Error checking missing column:', error);
        setIsShowWarning(true);
      }
    })();
  }, []);

  if (!calculationInfo || !activeFlow || !currentFlow || !source || !sourceType) return null;

  return (
    <Box
      id="type-conversion"
      sx={{
        paddingLeft: '0.5rem',
        paddingRight: '0.35rem',
        display: 'flex',
        flexDirection: 'column',
        paddingBottom: '20px',
        height: source[0] ? 'auto' : '100%',
      }}
    >
      <DataTypeConversionDropZone propKey={propKey}>
        {!source[0] ? (
          <Typography
            sx={{
              textAlign: 'left',
              mb: 1,
              paddingInline: '0.3rem',
              fontSize: '11px',
              color: '#999999',
              paddingLeft: '8px',
              paddingTop: '12px',
            }}
          >
            Drag a field or flow here
          </Typography>
        ) : (
          <ListItem
            sx={{
              border: isShowWarning ? '1px solid #850000ff !important' : '1px solid grey',
              borderRadius: '5px',
              paddingInline: '0.3rem',
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              height: '1.5rem',
              width: '100%',
              gap: '0.2rem',
              boxSizing: 'border-box',
              marginBottom: '0.5rem',
              '&:hover .hover-icon': {
                opacity: 1,
              },
            }}
          >
            <CloseRoundedIcon
              onClick={() => {
                if (disableUpdate) return;
                let fieldId = null;
                if (currentFlow[0].sourceType[0] === 'field') {
                  fieldId = currentFlow[0].source[0];
                }
                dispatch(resetSource(propKey, activeFlow, 0));
                if (fieldId) dispatch(deleteField(propKey, fieldId));
                dispatch(
                  addResultantDatatypeInCalculationFlow(
                    { uid: activeFlow, flow: activeFlow },
                    propKey,
                    '',
                    0,
                  ),
                );
              }}
              sx={{
                fontSize: fontSize.medium,
                cursor: 'pointer',
                color: palette.primary.contrastText,
                opacity: '0',
                '&:hover': {
                  color: disableUpdate ? 'inherit' : 'red',
                  opacity: '1',
                  transform: 'scale(1.1)',
                },
              }}
            />
            <span
              title={getInfo(source[0], sourceType[0])?.name}
              style={{
                width: '100px',
                flexGrow: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                color: palette.primary.contrastText,
              }}
            >
              {getInfo(source[0], sourceType[0])?.name}
            </span>
          </ListItem>
        )}
      </DataTypeConversionDropZone>

      {source[0] && (
        <Divider
          sx={{
            margin: '5px 0px',
          }}
        />
      )}

      {source[0] && (
        <>
          <Typography
            sx={{
              color: '#484848',
              fontSize: fontSize.large,
              fontWeight: 'bold',
              textAlign: 'left',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
            }}
          >
            New DataType
          </Typography>
          <Select
            value={source[1] ?? ''}
            onChange={changeHandler}
            displayEmpty
            inputProps={{ 'aria-label': 'Without label' }}
            IconComponent={KeyboardArrowDownIcon}
            sx={{
              height: '1.5rem',
              width: '100%',
              mt: 1,
              fontSize: fontSize.large,
              textAlign: 'left',
              '.MuiSelect-select': {
                minHeight: 'unset !important',
              },
              '.MuiSelect-icon': {
                fontSize: '18px',
                color: palette.primary.contrastText,
              },
            }}
            renderValue={(selected) => {
              if (!selected) {
                return <em>Select Conversion Type</em>;
              }

              return selected;
            }}
            MenuProps={{
              slotProps: {
                paper: {
                  sx: {
                    transform: `scale(${zoom}) !important`
                  }
                }
              },
            }}
          >
            {conversionOptions.map((option) => (
              <MenuItem key={option} value={option} disabled={isOptionDisabled(dataType, option)}>
                <Typography
                  sx={{
                    Width: 'auto',
                    overflow: 'hidden',
                    textoverflow: 'ellipses',
                    fontsize: '12px',
                  }}
                >
                  {option}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </>
      )}

      {source[0] && source[1] === 'date' && (
        <Box sx={{ marginTop: '15px' }}>
          <FormatBuilder
            updateFormatValue={updateFormatValue}
            yearFormat={source[4]}
            monthFormat={source[6]}
            dayFormat={source[8]}
            separator={getSeperator(source)}
            sources={source.slice(4)}
            isFlowUsedInOtherFlowsAsSource={disableUpdate}
            flow={currentFlow[0]?.flow}
          />
        </Box>
      )}

      {/* Toggle Switch */}
      {source[0] && (
        <Box>
          <Typography
            sx={{
              color: '#484848',
              fontSize: fontSize.large,
              fontWeight: 'bold',
              textAlign: 'left',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
              marginTop: '15px',
            }}
          >
            Handle Exception
          </Typography>
          <Switch
            sx={{ display: 'flex', justifyContent: 'start' }}
            checked={source[2]}
            onChange={handleToggle}
            color="primary"
          />
        </Box>
      )}

      {/* Input shown when toggle is enabled  */}
      {source[2] && source[0] && ['text', 'integer', 'decimal'].includes(source[1]) && (
        <Box>
          <Typography
            sx={{
              fontSize: 'fontsize.large',
              fontWeight: 'bold',
              textAlign: 'left',
              marginBottom: '5px',
              color: '#999999',
            }}
          >
            Fallback Value (optional)
          </Typography>
          <TextField
            value={source[3]}
            onChange={(e) => {
              const inputValue = e.target.value;

              if (source[1].toLowerCase() === 'integer') {
                if (/^[0-9]*$/.test(inputValue)) {
                  toggleInputHandler(e);
                }
              } else {
                toggleInputHandler(e);
              }
            }}
            type={['integer', 'decimal'].includes(source[1]) ? 'number' : 'text'}
            inputProps={{
              step: source[1] === 'decimal' ? 0.1 : 1,
              style: {
                paddingRight: '15px',
              },
            }}
            variant="outlined"
            size="small"
            placeholder={
              source[1] === 'integer' || source[1] === 'decimal'
                ? 'Enter the Number'
                : 'Enter the value'
            }
            sx={{
              width: '100%',
              mt: 0,
              backgroundColor: 'white',
              fontSize: fontSize.small,
              '.MuiOutlinedInput-root': {
                height: '1.5rem',
                fontSize: fontSize.small,
                padding: 0,
              },
              '.MuiOutlinedInput-input': {
                padding: '0 8px',
                height: '1.5rem',
                fontSize: fontSize.small,
                display: 'flex',
                alignItems: 'center',
              },
            }}
          />
        </Box>
      )}

      {/* Date Picker */}
      {source[0] && source[1] === 'date' && source[2] && (
        <Box>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              value={source[3] ? new Date(source[3]) : null}
              onChange={(newValue) => {
                if (!newValue) return;
                const formattedDate = newValue.toISOString().split('T')[0];
                setValue(formattedDate);
                dispatch(setSource(propKey, activeFlow, 0, formattedDate, 'text', 3));
              }}
              enableAccessibleFieldDOMStructure={false}
              slots={{
                textField: (params: any) => (
                  <TextField
                    {...params}
                    sx={{
                      '& .MuiInputBase-root': {
                        height: '1.5rem',
                        width: '100%',
                        marginRight: '28px',
                      },
                      // '& .MuiOutlinedInput-input': {
                      //   width: '100%',
                      // },
                    }}
                  />
                ),
              }}
            />
          </LocalizationProvider>
        </Box>
      )}

      {/* Input Toggle  */}
      {source[0] && source[2] && source[1] === 'boolean' && (
        <Box
          sx={{
            border: '1px solid Grey',
            borderRadius: '5px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'start',
            paddingLeft: '10px',
            height: '1.5rem',
            gap: '0.24rem',
          }}
        >
          <Typography>False</Typography>
          <Switch checked={source[3]} onChange={handleBoolean} />
          <Typography>True</Typography>
        </Box>
      )}
    </Box>
  );
}
